import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Site Captures (DroneDeploy) — Larry item 3.
//
// V1: paste a DroneDeploy view-only share link → the capture embeds in a
// full-screen iframe viewer for every project member.
// V2: company DroneDeploy connection → browse/import the org's flight
// inventory + a saved name-filter auto-imports new flights. Imported
// captures deep-link into DroneDeploy until a share link is pasted on.
//
// All mutations are AJAX — cards are inserted/replaced/removed in place
// (no full-page reloads, per CLAUDE.md). Card markup built here mirrors
// app/views/site_captures/_capture_card.html.erb — keep in sync.
export default class extends Controller {
  static targets = [
    "count", "grid", "empty",
    "importBtn", "autoImportBtn", "autoImportBtnLabel", "connectBtn",
    "formModal", "formTitle", "titleInput", "kindInput", "dateInput",
    "shareInput", "shareRequiredMark", "formError", "formSubmitBtn",
    "viewer", "viewerTitle", "viewerMeta", "viewerExternal", "viewerFrame",
    "importModal", "importFilter", "importList", "importSelectedCount", "importSubmitBtn",
    "autoImportModal", "autoFilterInput", "autoImportCheckbox", "autoImportStatus", "syncNowBtn",
    "deleteModal", "deleteName"
  ]

  static values = { baseUrl: String, projectId: Number }

  KIND_ICONS = {
    map: '<path stroke-linecap="round" stroke-linejoin="round" d="M9 6.75V15m0-8.25a1.5 1.5 0 0 1 3 0m-3 0a1.5 1.5 0 0 0 3 0m0 0V15m0-8.25a1.5 1.5 0 0 1 3 0m-3 0a1.5 1.5 0 0 0 3 0m0 0v8.25M6 15h12M6 15a2.25 2.25 0 0 1-2.25-2.25V6.75A2.25 2.25 0 0 1 6 4.5h12a2.25 2.25 0 0 1 2.25 2.25v6A2.25 2.25 0 0 1 18 15M6 15v3.75A2.25 2.25 0 0 0 8.25 21h7.5A2.25 2.25 0 0 0 18 18.75V15"/>',
    walkthrough: '<path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418"/>',
    panorama: '<path stroke-linecap="round" stroke-linejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z"/>',
    video: '<path stroke-linecap="round" stroke-linejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z"/>',
    other: '<path stroke-linecap="round" stroke-linejoin="round" d="M9 6.75V15m0-8.25a1.5 1.5 0 0 1 3 0m-3 0a1.5 1.5 0 0 0 3 0m0 0V15m0-8.25a1.5 1.5 0 0 1 3 0m-3 0a1.5 1.5 0 0 0 3 0m0 0v8.25M6 15h12M6 15a2.25 2.25 0 0 1-2.25-2.25V6.75A2.25 2.25 0 0 1 6 4.5h12a2.25 2.25 0 0 1 2.25 2.25v6A2.25 2.25 0 0 1 18 15M6 15v3.75A2.25 2.25 0 0 0 8.25 21h7.5A2.25 2.25 0 0 0 18 18.75V15"/>'
  }

  connect() {
    this._editingId = null
    this._deletingCard = null
    this._plans = []
    this._onKeydown = (e) => {
      if (e.key === "Escape" && this.hasViewerTarget && !this.viewerTarget.classList.contains("hidden")) {
        this.closeViewer()
      }
    }
    document.addEventListener("keydown", this._onKeydown)
    this._fetchDronedeployStatus()
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeydown)
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // ── DroneDeploy status → header buttons ───────────────────────────

  async _fetchDronedeployStatus() {
    try {
      const res = await fetch(`${this.baseUrlValue}/dronedeploy_status`, { headers: { Accept: "application/json" } })
      if (!res.ok) return
      this._ddStatus = await res.json()

      if (this._ddStatus.connected) {
        this.importBtnTarget.classList.remove("hidden")
        this.autoImportBtnTarget.classList.remove("hidden")
        if (this._ddStatus.filter) {
          this.autoImportBtnLabelTarget.textContent = `Auto-import: "${this._ddStatus.filter}"`
        }
      } else if (this._ddStatus.connect_url) {
        this.connectBtnTarget.href = this._ddStatus.connect_url
        this.connectBtnTarget.classList.remove("hidden")
      }
    } catch (_) { /* header buttons just stay hidden */ }
  }

  // ── Add / Edit modal ───────────────────────────────────────────────

  openAdd() {
    this._editingId = null
    this.formTitleTarget.textContent = "Add site capture"
    this.titleInputTarget.value = ""
    this.kindInputTarget.value = "map"
    this.shareInputTarget.value = ""
    this._setDate("")
    this.shareRequiredMarkTarget.classList.remove("hidden")
    this._setFormError(null)
    this.formModalTarget.classList.remove("hidden")
    this.titleInputTarget.focus()
  }

  openEdit(event) {
    const capture = this._captureFromEvent(event)
    if (!capture) return
    this._closeCardMenu(event)

    this._editingId = capture.id
    this.formTitleTarget.textContent = capture.embeddable ? "Edit site capture" : "Add share link"
    this.titleInputTarget.value = capture.title || ""
    this.kindInputTarget.value = capture.kind || "map"
    this.shareInputTarget.value = capture.share_url || ""
    this._setDate(capture.captured_on || "")
    // Synced captures may legitimately have no share link
    this.shareRequiredMarkTarget.classList.toggle("hidden", capture.source === "dronedeploy_sync")
    this._setFormError(null)
    this.formModalTarget.classList.remove("hidden")
    if (!capture.embeddable) this.shareInputTarget.focus()
  }

  closeForm() {
    this.formModalTarget.classList.add("hidden")
  }

  async submitForm() {
    const payload = {
      site_capture: {
        title: this.titleInputTarget.value.trim(),
        kind: this.kindInputTarget.value,
        share_url: this.shareInputTarget.value.trim(),
        captured_on: this.dateInputTarget.value
      }
    }
    if (!payload.site_capture.title) {
      return this._setFormError("Give the capture a title.")
    }

    const isEdit = !!this._editingId
    const url = isEdit ? `${this.baseUrlValue}/${this._editingId}` : this.baseUrlValue
    this.formSubmitBtnTarget.disabled = true
    try {
      const res = await fetch(url, {
        method: isEdit ? "PATCH" : "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify(payload)
      })
      const data = await res.json()
      if (!res.ok || !data.ok) {
        return this._setFormError(data.message || "Couldn't save the capture.")
      }

      if (isEdit) {
        this._replaceCard(data.capture)
        this._showToast("Capture updated")
      } else {
        this._insertCard(data.capture, { prepend: true })
        this._showToast("Capture added")
        // "Site Capture Added" is tracked server-side in the controller (same
        // as the DroneDeploy import path) — don't double-count it here.
      }
      this.closeForm()
    } catch (_) {
      this._setFormError("Couldn't save the capture — try again.")
    } finally {
      this.formSubmitBtnTarget.disabled = false
    }
  }

  // ── Viewer ─────────────────────────────────────────────────────────

  openCapture(event) {
    const capture = this._captureFromEvent(event)
    if (!capture) return

    if (capture.embeddable) {
      this.viewerTitleTarget.textContent = capture.title || "Site capture"
      this.viewerMetaTarget.textContent = [capture.kind_label, capture.captured_label].filter(Boolean).join(" · ")
      this.viewerExternalTarget.href = capture.open_url || capture.share_url
      this.viewerFrameTarget.src = capture.share_url
      this.viewerTarget.classList.remove("hidden")
      document.body.style.overflow = "hidden"
      track("Site Capture Viewed", { project_id: this.projectIdValue, kind: capture.kind, source: capture.source })
    } else if (capture.open_url) {
      window.open(capture.open_url, "_blank", "noopener")
      track("Site Capture Opened Externally", { project_id: this.projectIdValue, source: capture.source })
    }
  }

  closeViewer() {
    this.viewerTarget.classList.add("hidden")
    this.viewerFrameTarget.removeAttribute("src")
    document.body.style.overflow = ""
  }

  // ── Import from DroneDeploy ────────────────────────────────────────

  async openImport() {
    this.importModalTarget.classList.remove("hidden")
    this.importListTarget.innerHTML = '<p class="text-[13px] text-gray-400 text-center py-8">Loading flights…</p>'
    this.importFilterTarget.value = ""
    this._updateImportCount()
    try {
      const res = await fetch(`${this.baseUrlValue}/dronedeploy_plans`, { headers: { Accept: "application/json" } })
      const data = await res.json()
      if (!res.ok || !data.ok) {
        this.importListTarget.innerHTML =
          `<p class="text-[13px] text-rose-700 text-center py-8 px-4">${this._escape(data.message || "Couldn't reach DroneDeploy.")}</p>`
        return
      }
      this._plans = data.plans || []
      this._renderImportList()
    } catch (_) {
      this.importListTarget.innerHTML = '<p class="text-[13px] text-rose-700 text-center py-8">Couldn\'t reach DroneDeploy — try again.</p>'
    }
  }

  closeImport() {
    this.importModalTarget.classList.add("hidden")
  }

  filterImportList() {
    this._renderImportList()
  }

  _renderImportList() {
    const needle = (this.importFilterTarget.value || "").trim().toLowerCase()
    const plans = needle ? this._plans.filter((p) => (p.name || "").toLowerCase().includes(needle)) : this._plans

    if (plans.length === 0) {
      this.importListTarget.innerHTML = '<p class="text-[13px] text-gray-400 text-center py-8">No flights found.</p>'
      return
    }

    this.importListTarget.innerHTML = ""
    plans.forEach((plan) => {
      const row = document.createElement("label")
      row.className = `flex items-center gap-2.5 px-3 py-2 ${plan.imported ? "opacity-50" : "hover:bg-gray-50 cursor-pointer"}`
      row.innerHTML = `
        <input type="checkbox" value="${this._escape(plan.id)}" ${plan.imported ? "disabled" : ""}
               class="rounded text-brand focus:ring-brand ${plan.imported ? "" : "cursor-pointer"}" data-import-checkbox>
        <span class="min-w-0 flex-1 truncate text-[13px] text-gray-800">${this._escape(plan.name)}</span>
        <span class="text-[12px] text-gray-400 shrink-0">${this._escape(plan.captured_on || "")}</span>
        ${plan.imported ? '<span class="text-[11px] px-1.5 py-px rounded bg-gray-100 text-gray-500 shrink-0">Imported</span>' : ""}
      `
      const cb = row.querySelector("input")
      if (!plan.imported) cb.addEventListener("change", () => this._updateImportCount())
      this.importListTarget.appendChild(row)
    })
    this._updateImportCount()
  }

  _updateImportCount() {
    const n = this.importListTarget.querySelectorAll("[data-import-checkbox]:checked").length
    this.importSelectedCountTarget.textContent = n
  }

  async submitImport() {
    const ids = [...this.importListTarget.querySelectorAll("[data-import-checkbox]:checked")].map((cb) => cb.value)
    if (ids.length === 0) {
      return this._showToast("Pick at least one flight to import", "error")
    }
    this.importSubmitBtnTarget.disabled = true
    try {
      const res = await fetch(`${this.baseUrlValue}/dronedeploy_import`, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({ plan_ids: ids })
      })
      const data = await res.json()
      if (!res.ok || !data.ok) {
        return this._showToast(data.message || "Import failed", "error")
      }
      ;(data.captures || []).forEach((capture) => this._insertCard(capture, { prepend: true }))
      this._showToast(`Imported ${data.captures.length} ${data.captures.length === 1 ? "flight" : "flights"}`)
      this.closeImport()
    } catch (_) {
      this._showToast("Import failed — try again", "error")
    } finally {
      this.importSubmitBtnTarget.disabled = false
    }
  }

  // ── Auto-import settings ───────────────────────────────────────────

  openAutoImport() {
    const s = this._ddStatus || {}
    this.autoFilterInputTarget.value = s.filter || ""
    this.autoImportCheckboxTarget.checked = s.auto_import !== false
    this.autoImportStatusTarget.textContent = s.last_synced_at
      ? `Last checked: ${new Date(s.last_synced_at).toLocaleString()}`
      : "Hasn't synced yet."
    this.autoImportModalTarget.classList.remove("hidden")
  }

  closeAutoImport() {
    this.autoImportModalTarget.classList.add("hidden")
  }

  async saveAutoImport() {
    try {
      const res = await fetch(`${this.baseUrlValue}/dronedeploy_link`, {
        method: "POST",
        headers: this._jsonHeaders(),
        body: JSON.stringify({
          filter: this.autoFilterInputTarget.value.trim(),
          auto_import: this.autoImportCheckboxTarget.checked
        })
      })
      const data = await res.json()
      if (!res.ok || !data.ok) {
        return this._showToast(data.message || "Couldn't save auto-import settings", "error")
      }
      this._ddStatus = { ...(this._ddStatus || {}), filter: data.filter, auto_import: data.auto_import, linked: true }
      this.autoImportBtnLabelTarget.textContent = data.filter ? `Auto-import: "${data.filter}"` : "Auto-import"
      this._showToast(data.filter ? "Auto-import saved — matching flights are importing now" : "Auto-import settings saved")
      this.closeAutoImport()
      if (data.filter) this._refreshSoon()
    } catch (_) {
      this._showToast("Couldn't save auto-import settings", "error")
    }
  }

  async syncNow() {
    this.syncNowBtnTarget.disabled = true
    try {
      const res = await fetch(`${this.baseUrlValue}/dronedeploy_sync_now`, {
        method: "POST", headers: this._jsonHeaders()
      })
      const data = await res.json()
      if (!res.ok || !data.ok) {
        return this._showToast(data.message || "Couldn't start the sync", "error")
      }
      this._showToast("Sync started — new flights appear in a moment")
      this._refreshSoon()
    } catch (_) {
      this._showToast("Couldn't start the sync", "error")
    } finally {
      this.syncNowBtnTarget.disabled = false
    }
  }

  // Pull fresh cards a few seconds after a background sync kicks off.
  _refreshSoon() {
    setTimeout(async () => {
      try {
        const res = await fetch(`${this.baseUrlValue}.json`, { headers: { Accept: "application/json" } })
        if (!res.ok) return
        const data = await res.json()
        const existing = new Set([...this.gridTarget.querySelectorAll("[data-capture-id]")].map((el) => el.dataset.captureId))
        ;(data.captures || []).forEach((capture) => {
          if (!existing.has(String(capture.id))) this._insertCard(capture, { prepend: true })
        })
      } catch (_) { /* quiet */ }
    }, 6000)
  }

  // ── Delete ─────────────────────────────────────────────────────────

  openDelete(event) {
    const card = event.target.closest("[data-capture-card]")
    const capture = this._captureFromEvent(event)
    if (!card || !capture) return
    this._closeCardMenu(event)

    this._deletingCard = card
    this.deleteNameTarget.textContent = capture.title || "this capture"
    this.deleteModalTarget.classList.remove("hidden")
  }

  closeDelete() {
    this.deleteModalTarget.classList.add("hidden")
    this._deletingCard = null
  }

  async confirmDelete() {
    const card = this._deletingCard
    if (!card) return
    const id = card.dataset.captureId
    try {
      const res = await fetch(`${this.baseUrlValue}/${id}`, {
        method: "DELETE", headers: this._jsonHeaders()
      })
      const data = await res.json()
      if (!res.ok || !data.ok) {
        return this._showToast(data.message || "Couldn't remove the capture", "error")
      }
      card.remove()
      this._syncCountAndEmpty()
      this._showToast("Capture removed")
    } catch (_) {
      this._showToast("Couldn't remove the capture", "error")
    } finally {
      this.closeDelete()
    }
  }

  // ── Card building (mirrors _capture_card.html.erb) ────────────────

  _insertCard(capture, { prepend = false } = {}) {
    const card = this._buildCard(capture)
    if (prepend) this.gridTarget.prepend(card)
    else this.gridTarget.appendChild(card)
    this._syncCountAndEmpty()
  }

  _replaceCard(capture) {
    const old = this.gridTarget.querySelector(`[data-capture-id="${capture.id}"]`)
    const fresh = this._buildCard(capture)
    if (old) old.replaceWith(fresh)
    else this.gridTarget.prepend(fresh)
  }

  _buildCard(capture) {
    const div = document.createElement("div")
    div.setAttribute("data-capture-card", "")
    div.dataset.captureId = capture.id
    div.dataset.capture = JSON.stringify(capture)
    div.className = "rounded-xl border border-gray-200 bg-white overflow-hidden flex flex-col"

    const iconPath = this.KIND_ICONS[capture.kind] || this.KIND_ICONS.other
    const viewBadge = capture.embeddable
      ? '<span class="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-500/80 text-white">View inline</span>'
      : '<span class="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-black/40 text-white">DroneDeploy</span>'
    const sourceBadge = capture.source === "dronedeploy_sync"
      ? '<span class="ml-1 px-1.5 py-px rounded bg-gray-100 text-gray-500 text-[11px]">Imported</span>'
      : ""
    const openItem = capture.open_url
      ? `<a href="${this._escape(capture.open_url)}" target="_blank" rel="noopener"
            class="w-full flex items-center gap-2 px-3 py-2 text-[13px] hover:bg-gray-50 text-left">
           <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-gray-500"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/></svg>
           Open in DroneDeploy
         </a>`
      : ""

    div.innerHTML = `
      <button type="button" data-action="click->site-captures#openCapture"
              class="relative aspect-video w-full bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 flex items-center justify-center cursor-pointer group">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"
             class="w-10 h-10 text-white/40 group-hover:text-white/70 transition-colors">${iconPath}</svg>
        <span class="absolute bottom-2 left-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-black/40 text-white">${this._escape(capture.kind_label || capture.kind)}</span>
        ${viewBadge}
      </button>
      <div class="px-3 py-2 flex items-start gap-2">
        <div class="min-w-0 flex-1">
          <p class="text-[13px] font-medium text-gray-800 truncate" title="${this._escape(capture.title)}">${this._escape(capture.title)}</p>
          <p class="text-[12px] text-gray-500">${this._escape(capture.captured_label || "No date")}${sourceBadge}</p>
        </div>
        <div class="relative inline-block shrink-0" data-controller="dropdown">
          <button type="button" data-action="click->dropdown#toggle"
                  class="text-gray-400 hover:text-gray-600 px-1 py-0.5 rounded hover:bg-gray-100 cursor-pointer" aria-label="Capture actions">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5"><path fill-rule="evenodd" d="M10.5 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Zm0 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Zm0 6a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Z" clip-rule="evenodd"/></svg>
          </button>
          <div class="hidden absolute right-0 z-20 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-44 text-left" data-dropdown-target="menu">
            <button type="button" data-action="click->site-captures#openEdit"
                    class="w-full flex items-center gap-2 px-3 py-2 text-[13px] hover:bg-gray-50 text-left cursor-pointer">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-gray-500"><path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"/></svg>
              ${capture.embeddable ? "Edit" : "Add share link"}
            </button>
            ${openItem}
            <div class="border-t border-gray-100 my-1"></div>
            <button type="button" data-action="click->site-captures#openDelete"
                    class="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-rose-700 hover:bg-rose-50 text-left cursor-pointer">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 text-rose-500"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/></svg>
              Remove
            </button>
          </div>
        </div>
      </div>
    `
    return div
  }

  _syncCountAndEmpty() {
    const n = this.gridTarget.querySelectorAll("[data-capture-card]").length
    if (this.hasCountTarget) this.countTarget.textContent = n
    this.gridTarget.classList.toggle("hidden", n === 0)
    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", n > 0)
  }

  // ── Helpers ────────────────────────────────────────────────────────

  _captureFromEvent(event) {
    const card = event.target.closest("[data-capture-card]")
    if (!card) return null
    try {
      return JSON.parse(card.dataset.capture)
    } catch (_) {
      return null
    }
  }

  _closeCardMenu(event) {
    const menu = event.target.closest("[data-dropdown-target='menu']")
    if (menu) menu.classList.add("hidden")
  }

  _setDate(value) {
    const input = this.dateInputTarget
    if (input._flatpickr) input._flatpickr.setDate(value || null, false)
    else input.value = value || ""
  }

  _setFormError(message) {
    if (message) {
      this.formErrorTarget.textContent = message
      this.formErrorTarget.classList.remove("hidden")
    } else {
      this.formErrorTarget.textContent = ""
      this.formErrorTarget.classList.add("hidden")
    }
  }

  _jsonHeaders() {
    return {
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
      "Content-Type": "application/json",
      Accept: "application/json"
    }
  }

  _escape(str) {
    const div = document.createElement("div")
    div.textContent = str == null ? "" : String(str)
    return div.innerHTML
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
